import CustomTooltip from '../../../../shared/CustomTooltip';
import {
    AccessTime,
    Add,
    AddCircleOutline,
    CalendarMonth,
    Check,
    ChevronLeft,
    ChevronRight,
    Close,
    DeleteOutline,
    EventBusy,
    HelpOutline,
    LightbulbOutlined,
} from '@mui/icons-material';
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import {
    addMonths,
    differenceInCalendarDays,
    format,
    parseISO,
    startOfDay,
} from 'date-fns';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import type { DayButtonProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import {
    useFieldArray,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import {
    capitalizeName,
    dayToNumber,
    formatTime,
} from '../../../../../shared/utils';
import { Period } from '../../../../types/contractor';
import { ContractorProfile } from './schema';
import { z } from 'zod';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import { TimeArray } from '../../../Experiences/Owner/Property/TimeArray';
import { ModalProps } from '../../../../types/modal';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSelect from '../../../../shared/FormSelect';

const JS_WEEK_TO_DAY_KEY = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
] as const;

const getCss = (theme: Theme) => {
    const coral = theme.palette.custom.coral;

    return `
    .availability-calendar-surface .rdp-root {
        width: 100%;
        --rdp-outside-opacity: 0.35;
        --rdp-day_button-border-radius: 2px;
        --rdp-accent-color: ${theme.palette.primary.main};
        --rdp-accent-background-color: ${alpha(theme.palette.primary.main, 0.1)};
        --rdp-range_middle-background-color: transparent;
        --rdp-range_middle-color: inherit;
        --rdp-range_start-background: none;
        --rdp-range_end-background: none;
        --rdp-range_start-date-background-color: transparent;
        --rdp-range_end-date-background-color: transparent;
        --rdp-range_start-color: inherit;
        --rdp-range_end-color: inherit;
        --rdp-day_button-width: 100%;
        --rdp-day_button-height: 40px;
        --rdp-day-width: 100%;
        --rdp-day-height: 44px;
        font-size: 14px;
        color: ${theme.palette.text.primary};
        font-family: inherit;
    }

    .availability-calendar-surface .rdp-months {
        flex-direction: column;
        gap: 0;
        width: 100%;
    }

    .availability-calendar-surface .rdp-month {
        width: 100%;
    }

    .availability-calendar-surface .rdp-month_grid {
        width: 100%;
        border: 1px solid ${theme.palette.divider};
        border-collapse: collapse;
        table-layout: fixed;
    }

    .availability-calendar-surface .rdp-weekdays {
        border-bottom: 1px solid ${theme.palette.divider};
    }

    .availability-calendar-surface .rdp-weekday {
        font-size: 0.7rem;
        font-weight: 600;
        color: ${alpha(theme.palette.text.primary, 0.55)};
        border-right: 1px solid ${theme.palette.divider};
        padding: 8px 0;
        opacity: 1;
    }

    .availability-calendar-surface .rdp-weekday:last-child {
        border-right: none;
    }

    .availability-calendar-surface .rdp-day {
        border-right: 1px solid ${theme.palette.divider};
        border-bottom: 1px solid ${theme.palette.divider};
        padding: 0;
        vertical-align: middle;
    }

    .availability-calendar-surface .rdp-week:last-child .rdp-day {
        border-bottom: none;
    }

    .availability-calendar-surface .rdp-week .rdp-day:last-child {
        border-right: none;
    }

    /* Draft selected range: soft coral cells, rectangular (img 1) */
    .availability-calendar-surface .rdp-range_start,
    .availability-calendar-surface .rdp-range_middle,
    .availability-calendar-surface .rdp-range_end {
        background-color: ${alpha(coral, 0.22)} !important;
        background-image: none !important;
    }

    .availability-calendar-surface .rdp-range_start .rdp-day_button,
    .availability-calendar-surface .rdp-range_middle .rdp-day_button,
    .availability-calendar-surface .rdp-range_end .rdp-day_button {
        background-color: transparent !important;
        background-image: none !important;
        border-radius: 0 !important;
        border: none !important;
        box-shadow: none !important;
        width: 100%;
        min-height: 40px;
        color: ${coral} !important;
    }

    .availability-calendar-surface .rdp-selected .rdp-day_button {
        font-weight: 700;
        font-size: 14px;
    }

    /* Saved unavailable: no cell fill — indicator is the red dot only (img 2) */
    .availability-calendar-surface .availability-rdp-timeoff .rdp-day_button {
        background-color: transparent !important;
        background-image: none !important;
    }

    .availability-calendar-surface .rdp-today:not(.rdp-outside) {
        background-color: ${alpha(theme.palette.primary.main, 0.08)};
    }

    /* Today: fixed square dimensions so the badge stays a true circle */
    .availability-calendar-surface
        .rdp-today:not(.rdp-range_start):not(.rdp-range_end):not(.rdp-range_middle):not(.rdp-outside)
        .rdp-day_button {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
        margin-left: auto;
        margin-right: auto;
        padding: 0 !important;
        background-color: ${theme.palette.primary.main};
        color: #fff !important;
        border-radius: 50% !important;
        box-shadow: none;
    }

    .availability-calendar-surface .rdp-month_caption {
        justify-content: center;
        margin-bottom: 8px;
    }

    .availability-daypicker--readonly .availability-calendar-surface {
        pointer-events: none;
    }

    .availability-daypicker--readonly
        .availability-calendar-surface
        .rdp-month_caption,
    .availability-daypicker--readonly
        .availability-calendar-surface
        .rdp-dropdown_root,
    .availability-daypicker--readonly
        .availability-calendar-surface
        .rdp-dropdown,
    .availability-daypicker--readonly
        .availability-calendar-surface
        .rdp-caption_label,
    .availability-daypicker--readonly .availability-calendar-surface select {
        pointer-events: auto;
        cursor: pointer;
    }
`;
};

const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
] as const;

type DayOfWeek = (typeof daysOfWeek)[number];

const dayOrderByWeek = Object.fromEntries(
    daysOfWeek.map((day, index) => [day, index]),
) as Record<DayOfWeek, number>;

function formatWeeklyAvailabilitySummary(availableDays: string[]): string {
    if (!availableDays?.length) {
        return 'No weekly pattern selected';
    }
    const groups: (typeof daysOfWeek)[number][][] = [];
    let i = 0;
    while (i < daysOfWeek.length) {
        if (!availableDays.includes(daysOfWeek[i])) {
            i++;
            continue;
        }
        const start = i;
        while (i < daysOfWeek.length && availableDays.includes(daysOfWeek[i])) {
            i++;
        }
        groups.push(daysOfWeek.slice(start, i));
    }

    const fmtGroup = (g: (typeof daysOfWeek)[number][]) => {
        if (g.length === 1) {
            return capitalizeName(g[0]);
        }
        if (g.length === 2) {
            return `${capitalizeName(g[0])} and ${capitalizeName(g[1])}`;
        }
        return `${capitalizeName(g[0])} to ${capitalizeName(g[g.length - 1])}`;
    };

    if (groups.length === 1 && groups[0].length === 7) {
        return 'every day';
    }

    return groups.map(fmtGroup).join(' and ');
}

function dateInAnyPeriod(date: Date, periods: Period[]): boolean {
    const t = date.getTime();
    return periods.some((p) => {
        const from = new Date(`${p.from}T00:00:00`).getTime();
        const to = new Date(`${p.to}T00:00:00`).getTime();
        return t >= from && t <= to;
    });
}

function formatPeriodRangeLabel(p: Period): string {
    const fromD = new Date(`${p.from}T00:00:00`);
    const toD = new Date(`${p.to}T00:00:00`);
    const a = format(fromD, 'd MMMM yyyy');
    const b = format(toD, 'd MMMM yyyy');
    return a === b ? a : `${a} - ${b}`;
}

function periodDurationDays(p: Period): number {
    const fromD = new Date(`${p.from}T00:00:00`);
    const toD = new Date(`${p.to}T00:00:00`);
    const n = differenceInCalendarDays(toD, fromD) + 1;
    return n < 1 ? 1 : n;
}

export function getUnavailableDays(availableDays: string[]): number[] {
    const availableDayNumbers = new Set(
        availableDays.map((day) => dayToNumber[day]),
    );

    return Object.values(dayToNumber).filter(
        (dayNumber) => !availableDayNumbers.has(dayNumber),
    );
}

interface AvailabilityProps {
    isEditing: boolean;
    compactShell?: boolean;
}

const Availability = ({
    isEditing,
    compactShell = false,
}: AvailabilityProps) => {
    const { control, watch, setValue } =
        useFormContext<z.infer<typeof ContractorProfile>>();

    const theme = useTheme();
    const calendarRef = useRef<HTMLDivElement | null>(null);

    const availableDays = useWatch({ name: 'availableDays', control });
    const unavailablePeriods = watch('unavailablePeriods') as Period[];

    const [draftRange, setDraftRange] = useState<DateRange | undefined>();
    const [displayMonth, setDisplayMonth] = useState(() => new Date());
    const [calendarView, setCalendarView] = useState<'month' | 'week' | 'list'>(
        'month',
    );
    const [unavailableDatesTab, setUnavailableDatesTab] = useState<
        'upcoming' | 'past'
    >('upcoming');

    useEffect(() => {
        if (!isEditing) {
            setDraftRange(undefined);
        }
    }, [isEditing]);

    const tomorrow = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const timeOffMatchers = useMemo(
        () =>
            unavailablePeriods.map((p) => ({
                from: parseISO(p.from),
                to: parseISO(p.to),
            })),
        [unavailablePeriods],
    );

    const addPeriodFromRange = (range: DateRange | undefined) => {
        if (!range?.from) return;

        const newPeriod: Period = {
            from: format(range.from, 'yyyy-MM-dd'),
            to: format(range.to ?? range.from, 'yyyy-MM-dd'),
            demo_id: Math.random(),
        };

        const updatedPeriods = [...unavailablePeriods];

        updatedPeriods.push(newPeriod);
        updatedPeriods.sort((a, b) => a.from.localeCompare(b.from));
        setValue('unavailablePeriods', updatedPeriods, { shouldDirty: true });

        setDraftRange(undefined);
    };

    const unavailableDays = getUnavailableDays(
        availableDays.map((availability) => availability.day),
    );

    const toggleDay = (
        day:
            | 'monday'
            | 'tuesday'
            | 'wednesday'
            | 'thursday'
            | 'friday'
            | 'saturday'
            | 'sunday',
    ) => {
        if (!isEditing) return;
        const updatedAvailableDays = availableDays.find((d) => d.day === day)
            ? availableDays.filter((d) => d.day !== day)
            : [...availableDays, { day, periods: [] }];

        updatedAvailableDays.sort(
            (a, b) => dayOrderByWeek[a.day] - dayOrderByWeek[b.day],
        );
        setValue('availableDays', updatedAvailableDays, { shouldDirty: true });
    };

    const scrollToCalendar = () => {
        calendarRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    };

    const weeklySummary = formatWeeklyAvailabilitySummary(
        availableDays.map((availability) => availability.day),
    );

    const futurePeriods = useMemo(
        () =>
            unavailablePeriods
                .filter(
                    (p: Period) =>
                        new Date(`${p.to}T00:00:00`) >= startOfDay(new Date()),
                )
                .sort((a, b) => a.from.localeCompare(b.from)),
        [unavailablePeriods],
    );

    const pastPeriods = useMemo(
        () =>
            unavailablePeriods
                .filter(
                    (p: Period) =>
                        new Date(`${p.to}T00:00:00`) < startOfDay(new Date()),
                )
                .sort((a, b) => b.to.localeCompare(a.to)),
        [unavailablePeriods],
    );

    useEffect(() => {
        if (unavailablePeriods.length === 0) {
            setUnavailableDatesTab('upcoming');
            return;
        }
        if (pastPeriods.length === 0 && unavailableDatesTab === 'past') {
            setUnavailableDatesTab('upcoming');
        }
    }, [unavailablePeriods.length, pastPeriods.length, unavailableDatesTab]);

    const displayedUnavailablePeriods =
        pastPeriods.length > 0 && unavailableDatesTab === 'past'
            ? pastPeriods
            : futurePeriods;

    const unavailableListNeedsScroll = displayedUnavailablePeriods.length > 2;

    const unavailableListScrollMaxPx = 200;

    const renderUnavailablePeriodCard = (p: Period, index: number) => (
        <Card
            key={`${p.from}-${p.to}-${p.demo_id ?? index}`}
            variant='outlined'
            sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderColor: alpha(theme.palette.text.primary, 0.12),
                flexShrink: 0,
            }}
        >
            <EventBusy color='error' sx={{ fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {formatPeriodRangeLabel(p)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    {periodDurationDays(p)}{' '}
                    {periodDurationDays(p) === 1 ? 'day' : 'days'}
                </Typography>
            </Box>
            {isEditing ? (
                <IconButton
                    size='small'
                    aria-label='Remove unavailable dates'
                    onClick={() => {
                        setValue(
                            'unavailablePeriods',
                            unavailablePeriods.filter(
                                (period: Period) =>
                                    period.demo_id !== p.demo_id,
                            ),
                            { shouldDirty: true },
                        );
                    }}
                >
                    <DeleteOutline fontSize='small' />
                </IconButton>
            ) : null}
        </Card>
    );

    const AvailabilityDayButton = useCallback(
        (props: DayButtonProps) => {
            const { day, modifiers, children, ...buttonProps } = props;
            const inTimeOff = dateInAnyPeriod(day.date, unavailablePeriods);
            const dayKey =
                JS_WEEK_TO_DAY_KEY[
                    day.date.getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6
                ];
            const onWeeklyPattern =
                availableDays.find((d) => d.day === dayKey) && !inTimeOff;

            const inSelectedRange = Boolean(modifiers.selected);
            const coral = theme.palette.custom.coral;

            const showCoralDot = inSelectedRange;
            const showTimeOffDot = inTimeOff && !inSelectedRange;
            const showGreenDot =
                onWeeklyPattern &&
                !modifiers.outside &&
                !inTimeOff &&
                !inSelectedRange;

            return (
                <button type='button' {...buttonProps}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.25,
                            minHeight: 40,
                            width: '100%',
                        }}
                    >
                        <Box
                            component='span'
                            sx={{
                                lineHeight: 1.1,
                                color: modifiers.outside
                                    ? 'text.disabled'
                                    : inSelectedRange
                                      ? coral
                                      : inTimeOff
                                        ? 'text.primary'
                                        : 'inherit',
                                fontWeight: inSelectedRange ? 700 : 500,
                            }}
                        >
                            {children}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.25,
                                minHeight: 6,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {showCoralDot ? (
                                <Box
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        bgcolor: coral,
                                    }}
                                />
                            ) : showTimeOffDot ? (
                                <Box
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        bgcolor: 'error.main',
                                    }}
                                />
                            ) : showGreenDot ? (
                                <Box
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                    }}
                                />
                            ) : null}
                        </Box>
                    </Box>
                </button>
            );
        },
        [availableDays, unavailablePeriods, theme],
    );

    const sectionTitle = (n: string, title: string) => (
        <Typography
            variant='caption'
            component='span'
            sx={{
                color: 'text.secondary',
                fontWeight: 500,
                letterSpacing: 0.02,
            }}
        >
            {n} {title}
        </Typography>
    );

    const availabilityHelpTooltip = (
        <CustomTooltip
            title={
                <Typography component='div'>
                    <List dense>
                        <ListItem sx={{ display: 'list-item' }}>
                            Weekly availability sets which days you usually
                            accept bookings.
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            Time off blocks specific dates and declines requests
                            for those periods.
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            Only future time off is listed below.
                        </ListItem>
                    </List>
                </Typography>
            }
            placement='right'
        >
            <HelpOutline
                sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                    verticalAlign: 'middle',
                }}
            />
        </CustomTooltip>
    );

    const leftColumn = (
        <Stack spacing={3} sx={{ minWidth: 0, minHeight: 0 }}>
            <Stack spacing={1.5}>
                <Stack
                    direction='row'
                    alignItems='center'
                    spacing={0.75}
                    sx={{ flexWrap: 'wrap' }}
                >
                    <Typography
                        variant='caption'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            letterSpacing: 0.02,
                        }}
                    >
                        1. Weekly availability
                    </Typography>
                    {availabilityHelpTooltip}
                </Stack>
                <Typography variant='body2' color='text.secondary'>
                    Select the days your organisation is usually available.
                </Typography>
                <Stack direction='row' flexWrap='wrap' gap={1}>
                    {daysOfWeek.map((day) => {
                        const isSelected = availableDays.find(
                            (d) => d.day === day,
                        );
                        const short = capitalizeName(day).slice(0, 3);
                        return (
                            <Button
                                key={day}
                                variant={isSelected ? 'contained' : 'outlined'}
                                onClick={() => toggleDay(day)}
                                disabled={!isEditing}
                                disableElevation
                                sx={{
                                    minWidth: 52,
                                    px: 1,
                                    py: 1,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    ...(isSelected
                                        ? {}
                                        : {
                                              borderColor: alpha(
                                                  theme.palette.text.primary,
                                                  0.22,
                                              ),
                                              bgcolor: 'background.paper',
                                          }),
                                }}
                            >
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    spacing={0.5}
                                >
                                    {isSelected ? (
                                        <Check sx={{ fontSize: 16 }} />
                                    ) : null}
                                    <span>{short}</span>
                                </Stack>
                            </Button>
                        );
                    })}
                </Stack>
                {availableDays.length > 0 && (
                    <>
                        <Typography variant='body2' color='text.secondary'>
                            Select your operating hours (optional)
                        </Typography>
                        <AvailableDays isEditing={isEditing} />
                    </>
                )}
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                >
                    <CalendarMonth
                        sx={{
                            color: 'primary.main',
                            flexShrink: 0,
                            fontSize: 22,
                        }}
                    />
                    <Typography variant='body2' color='text.primary'>
                        Your organisation is available{' '}
                        <Box component='span' sx={{ fontWeight: 700 }}>
                            {weeklySummary}
                        </Box>
                        .
                    </Typography>
                </Paper>
                <Stack spacing={1}>
                    <Typography
                        variant='caption'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            letterSpacing: 0.02,
                        }}
                    >
                        Job request cut-off
                    </Typography>
                    <FormSelect
                        name='cutoff'
                        control={control}
                        options={[
                            { value: 0, label: 'Same day' },
                            { value: 1, label: '1 business day' },
                            { value: 2, label: '2 business days' },
                            { value: 3, label: '3 business days' },
                            { value: 4, label: '4 business days' },
                            { value: 5, label: '5 business days' },
                            { value: 6, label: '6 business days' },
                            { value: 7, label: '7 business days' },
                        ]}
                        disabled={!isEditing}
                    />
                    <FormHelperText>
                        Business days before a job start date that requests must
                        be submitted, based on your available days.
                    </FormHelperText>
                </Stack>
            </Stack>

            <Divider sx={{ borderColor: 'divider' }} />

            <Stack
                spacing={1.5}
                sx={{
                    pointerEvents: 'auto',
                    minWidth: 0,
                    minHeight: 0,
                }}
            >
                {sectionTitle('2.', 'Unavailable dates (time off)')}
                <Typography variant='body2' color='text.secondary'>
                    Add dates or date ranges when your organisation is not
                    available.
                </Typography>

                {pastPeriods.length > 0 ? (
                    <Tabs
                        value={unavailableDatesTab}
                        onChange={(_, v) =>
                            setUnavailableDatesTab(v as 'upcoming' | 'past')
                        }
                        variant='fullWidth'
                        sx={{
                            minHeight: 40,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pointerEvents: 'auto',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                            },
                        }}
                    >
                        <Tab
                            value='upcoming'
                            label={`Upcoming (${futurePeriods.length})`}
                        />
                        <Tab
                            value='past'
                            label={`Past (${pastPeriods.length})`}
                        />
                    </Tabs>
                ) : null}

                {futurePeriods.length === 0 && pastPeriods.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                        No time off added.
                    </Typography>
                ) : displayedUnavailablePeriods.length > 0 ? (
                    <Box
                        sx={{
                            pointerEvents: 'auto',
                            touchAction: 'pan-y',
                            width: '100%',
                            minHeight: 0,
                            flexShrink: 1,
                            alignSelf: 'stretch',
                            ...(unavailableListNeedsScroll
                                ? {
                                      height: unavailableListScrollMaxPx,
                                      maxHeight: unavailableListScrollMaxPx,
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      pr: 0.5,
                                      WebkitOverflowScrolling: 'touch',
                                  }
                                : {
                                      overflowY: 'visible',
                                      overflowX: 'visible',
                                  }),
                        }}
                    >
                        <Stack spacing={1}>
                            {displayedUnavailablePeriods.map(
                                (p: Period, index: number) =>
                                    renderUnavailablePeriodCard(p, index),
                            )}
                        </Stack>
                    </Box>
                ) : (
                    <Typography variant='body2' color='text.secondary'>
                        {unavailableDatesTab === 'past'
                            ? 'No past time off in this list.'
                            : 'No upcoming time off added.'}
                    </Typography>
                )}

                {isEditing ? (
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                        }}
                    >
                        <LightbulbOutlined
                            sx={{
                                color: 'info.main',
                                fontSize: 22,
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                            <Box
                                component='span'
                                sx={{ fontWeight: 700, color: 'info.dark' }}
                            >
                                Tip:{' '}
                            </Box>
                            Click a date or drag on the calendar to add or
                            remove unavailable dates. Use the trash icon on a
                            range to delete it.
                        </Typography>
                    </Paper>
                ) : null}
            </Stack>
        </Stack>
    );

    const legendItem = (
        dot: 'green' | 'red' | 'square' | 'today',
        label: string,
    ) => (
        <Stack direction='row' alignItems='center' spacing={1}>
            {dot === 'green' ? (
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                    }}
                />
            ) : dot === 'red' ? (
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                    }}
                />
            ) : dot === 'square' ? (
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 0.5,
                        bgcolor: alpha(theme.palette.custom.coral, 0.28),
                        border: `1px solid ${alpha(theme.palette.custom.coral, 0.65)}`,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'primary.contrastText',
                        bgcolor: 'primary.main',
                    }}
                >
                    {format(new Date(), 'd')}
                </Box>
            )}
            <Typography variant='caption' color='text.secondary'>
                {label}
            </Typography>
        </Stack>
    );

    const rightColumn = (
        <Stack spacing={1.5} ref={calendarRef} sx={{ minWidth: 0 }}>
            {sectionTitle('3.', 'Calendar')}
            <Typography variant='body2' color='text.secondary'>
                {isEditing
                    ? 'Click or drag to select unavailable dates.'
                    : 'Unavailable dates are in red on the calendar.'}
            </Typography>
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                flexWrap='wrap'
                gap={1.5}
            >
                <ButtonGroup
                    variant='outlined'
                    size='small'
                    sx={{
                        '& .MuiButtonBase-root': {
                            px: 1,
                            py: 0.5,
                            lineHeight: 1.2,
                        },
                    }}
                >
                    <IconButton
                        aria-label='Previous month'
                        size='small'
                        onClick={() => setDisplayMonth((m) => addMonths(m, -1))}
                    >
                        <ChevronLeft fontSize='small' />
                    </IconButton>
                    <Button
                        size='small'
                        onClick={() => setDisplayMonth(new Date())}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 72,
                            color: 'text.primary',
                            borderLeft: `1px solid ${theme.palette.divider} !important`,
                            borderRight: `1px solid ${theme.palette.divider} !important`,
                        }}
                    >
                        Today
                    </Button>
                    <IconButton
                        aria-label='Next month'
                        size='small'
                        onClick={() => setDisplayMonth((m) => addMonths(m, 1))}
                    >
                        <ChevronRight fontSize='small' />
                    </IconButton>
                </ButtonGroup>
                <ToggleButtonGroup
                    exclusive
                    color='primary'
                    size='small'
                    value={calendarView}
                    onChange={(_, v) => {
                        if (v) setCalendarView(v);
                    }}
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 1.75,
                            py: 0.65,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
                            '&.Mui-disabled': {
                                borderColor: alpha(
                                    theme.palette.text.primary,
                                    0.12,
                                ),
                            },
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            border: `2px solid ${theme.palette.primary.main}`,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            fontWeight: 700,
                            zIndex: 1,
                            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
                            '&:hover': {
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.14,
                                ),
                            },
                        },
                    }}
                >
                    <ToggleButton value='month'>Month</ToggleButton>
                    <ToggleButton value='week' disabled>
                        Week
                    </ToggleButton>
                    <ToggleButton value='list' disabled>
                        List
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                    bgcolor: 'background.paper',
                }}
                className={
                    isEditing ? undefined : 'availability-daypicker--readonly'
                }
            >
                <Box className='availability-calendar-surface'>
                    <DayPicker
                        mode='range'
                        weekStartsOn={1}
                        numberOfMonths={1}
                        month={displayMonth}
                        onMonthChange={setDisplayMonth}
                        captionLayout='dropdown'
                        hideNavigation
                        formatters={{
                            formatWeekdayName: (date) => format(date, 'EEE'),
                        }}
                        selected={isEditing ? draftRange : undefined}
                        onSelect={
                            isEditing
                                ? (range) => setDraftRange(range)
                                : undefined
                        }
                        onDayClick={
                            isEditing
                                ? undefined
                                : (_date, _modifiers, e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                  }
                        }
                        disabled={[
                            ...timeOffMatchers,
                            { dayOfWeek: unavailableDays },
                            { before: tomorrow },
                        ]}
                        modifiers={{
                            timeOff: timeOffMatchers,
                        }}
                        modifiersClassNames={{
                            timeOff: 'availability-rdp-timeoff',
                        }}
                        modifiersStyles={{
                            disabled: {
                                color: alpha(theme.palette.text.primary, 0.45),
                            },
                        }}
                        components={{
                            DayButton: AvailabilityDayButton,
                        }}
                    />
                </Box>
                <Stack
                    direction='row'
                    flexWrap='wrap'
                    columnGap={2}
                    rowGap={1}
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
                >
                    {legendItem('green', 'Available')}
                    {legendItem('red', 'Unavailable')}
                    {legendItem('square', 'Selected range')}
                    {legendItem('today', 'Today')}
                </Stack>
            </Paper>

            {isEditing ? (
                <Stack
                    direction='row'
                    sx={{
                        justifyContent: 'flex-end',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant='outlined'
                        onClick={() => setDraftRange(undefined)}
                        disabled={!draftRange?.from}
                    >
                        Clear selection
                    </Button>
                    <Button
                        variant='contained'
                        onClick={() => addPeriodFromRange(draftRange)}
                        disabled={!draftRange?.from}
                    >
                        Add range
                    </Button>
                </Stack>
            ) : null}
        </Stack>
    );

    return (
        <Stack
            sx={{
                alignItems: 'stretch',
                py: compactShell ? 0 : '30px',
                width: '100%',
            }}
            spacing={2}
        >
            <style>{getCss(theme)}</style>
            {!compactShell ? (
                <Typography
                    sx={{
                        fontWeight: 'bold',
                        color: 'text.primary',
                        alignSelf: 'flex-start',
                    }}
                >
                    Availability
                </Typography>
            ) : null}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6} sx={{ minWidth: 0, minHeight: 0 }}>
                    {leftColumn}
                </Grid>
                <Grid item xs={12} md={6} sx={{ minWidth: 0, minHeight: 0 }}>
                    {rightColumn}
                </Grid>
            </Grid>
        </Stack>
    );
};

const AvailableDays = ({ isEditing }: { isEditing: boolean }) => {
    const { control } = useFormContext<z.infer<typeof ContractorProfile>>();
    const availableDays = useWatch({ name: 'availableDays', control });

    return (
        <Paper variant='outlined' sx={{ borderRadius: 2 }}>
            {availableDays.map((availability, index) => (
                <React.Fragment key={availability.day}>
                    <IndividualDay
                        availability={availability}
                        index={index}
                        isEditing={isEditing}
                    />
                    {index !== availableDays.length - 1 && (
                        <Divider sx={{ border: 'none' }} />
                    )}
                </React.Fragment>
            ))}
        </Paper>
    );
};

const IndividualDay = ({
    availability,
    index,
    isEditing,
}: {
    availability: { day: string; periods: { from: string; to: string }[] };
    index: number;
    isEditing: boolean;
}) => {
    const { control } = useFormContext<z.infer<typeof ContractorProfile>>();
    const { fields, append, remove, update } = useFieldArray({
        name: `availableDays.${index}.periods`,
        control,
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [hovering, setHovering] = useState(false);

    const handleAdd = () => {
        setEditingIndex(null);
        setModalOpen(true);
    };

    const handleEdit = (periodIndex: number) => {
        setEditingIndex(periodIndex);
        setModalOpen(true);
    };

    const handleSave = (period: { from: string; to: string }) => {
        if (editingIndex !== null) {
            update(editingIndex, period);
        } else {
            append(period);
        }
        setModalOpen(false);
    };

    return (
        <>
            <Grid container sx={{ p: 1, alignItems: 'center' }} gap={1}>
                <Grid
                    item
                    xs={2}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                >
                    <CalendarMonth fontSize='small' />
                    <Typography sx={{ fontWeight: 'bold' }}>
                        {capitalizeName(availability.day).slice(0, 3)}
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        cursor: isEditing ? 'pointer' : 'default',
                        alignItems: 'center',
                    }}
                    onClick={() => {
                        if (!isEditing) return;
                        handleAdd();
                    }}
                    onMouseEnter={() => {
                        if (!isEditing) return;
                        setHovering(true);
                    }}
                    onMouseLeave={() => {
                        if (!isEditing) return;
                        setHovering(false);
                    }}
                >
                    {fields.length === 0 && (
                        <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary' }}
                        >
                            All Day
                        </Typography>
                    )}
                    {fields.map((period, periodIndex) => (
                        <Chip
                            key={period.id}
                            label={`${formatTime(period.from)} - ${formatTime(period.to)}`}
                            onClick={
                                isEditing
                                    ? (e) => {
                                          e.stopPropagation();
                                          handleEdit(periodIndex);
                                      }
                                    : undefined
                            }
                            onDelete={
                                isEditing
                                    ? () => remove(periodIndex)
                                    : undefined
                            }
                            sx={(theme) => ({
                                borderRadius: 2,
                                fontWeight: 'bold',
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08,
                                ),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            })}
                        />
                    ))}
                    <Fade in={hovering}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                px: 2,
                                borderRadius: 2,
                                backgroundColor: 'custom.cardActionBackground',
                                color: 'text.secondary',
                            }}
                        >
                            <Add fontSize='small' />
                            <Typography
                                variant='overline'
                                sx={{ fontWeight: 'bold' }}
                            >
                                Add time period
                            </Typography>
                        </Box>
                    </Fade>
                </Grid>
                {isEditing && (
                    <Grid item xs={1}>
                        <Tooltip title='Add available hours'>
                            <IconButton
                                color='primary'
                                onClick={() => handleAdd()}
                                sx={{ ml: 'auto' }}
                                size='small'
                            >
                                <AddCircleOutline fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
            <OperatingHoursModal
                key={`${availability.day}-${editingIndex}`}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                availability={availability}
                period={
                    editingIndex !== null ? fields[editingIndex] : undefined
                }
                editingIndex={editingIndex}
                onSave={handleSave}
            />
        </>
    );
};

const OperatingHoursModal = ({
    availability,
    open,
    onClose,
    period,
    editingIndex,
    onSave,
}: {
    availability: { day: string; periods: { from: string; to: string }[] };
    period?: { from: string; to: string };
    editingIndex: number | null;
    onSave: (period: { from: string; to: string }) => void;
} & ModalProps) => {
    const { day, periods } = availability;

    const PeriodSchema = z
        .object({
            from: z.string().min(1, 'Required'),
            to: z.string().min(1, 'Required'),
        })
        .refine((data) => data.from < data.to, {
            message: 'End time must be after start time',
            path: ['to'],
        });

    const { control, handleSubmit, reset } = useForm<
        z.infer<typeof PeriodSchema>
    >({
        resolver: zodResolver(PeriodSchema),
        defaultValues: { from: '09:00', to: '17:00' },
        values: period,
    });

    const from = useWatch({ control, name: 'from' });
    const to = useWatch({ control, name: 'to' });

    const overlapError = periods.some(
        (period, index) =>
            index !== editingIndex && from < period.to && period.from < to,
    );

    const onSubmit = (data: z.infer<typeof PeriodSchema>) => {
        if (overlapError) return;

        onSave(data);
        reset();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
            <DialogTitle>
                Add your hours for {capitalizeName(day)}
                <IconButton
                    aria-label='close'
                    onClick={onClose}
                    size='small'
                    sx={{ color: 'text.primary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Start time</InputLabel>
                        <FormAutocomplete
                            name='from'
                            control={control}
                            options={TimeArray}
                            startAdornment={
                                <InputAdornment position='start'>
                                    <AccessTime fontSize='small' />
                                </InputAdornment>
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>End time</InputLabel>
                        <FormAutocomplete
                            name='to'
                            control={control}
                            options={TimeArray}
                            startAdornment={
                                <InputAdornment position='start'>
                                    <AccessTime fontSize='small' />
                                </InputAdornment>
                            }
                        />
                    </Grid>
                </Grid>
                {overlapError && (
                    <FormHelperText error sx={{ mt: 1 }}>
                        This time period overlaps with an existing period
                    </FormHelperText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant='contained'
                    onClick={handleSubmit(onSubmit)}
                    disabled={overlapError}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Availability;
